import React, { PureComponent, SyntheticEvent } from 'react';
import StepCard from '../../../components/StepCard';
import { Button } from 'Common/components/Atoms/Button';
import { CardHeader } from 'Common/components/Atoms/CardHeader';
import { ContentLoader } from 'Common/components/Atoms/ContentLoader';
import { Div } from 'Common/components/Atoms/Div';
import { ExpertiseLevels } from 'Common/components/Organisms/ExpertiseLevels';
import { Flex } from 'Common/components/Atoms/Flex';
import { InputField } from 'Common/components/Atoms/InputField';
import { Loading } from 'Common/components/Atoms/Loading';
import { Modal } from 'Common/components/Molecules/Modal';
import { Toast } from 'Common/components/Molecules/Toast';
import { Tooltip } from 'Common/components/Atoms/Tooltip';
import { Text } from 'Common/components/Atoms/Text';
import { Theme } from '@mui/material/styles';
import withTheme from '@mui/styles/withTheme';
import { actions as signupsActions } from '../../../reducers/signups';
import { actions as accountActions } from '../../../reducers/Account';
import {
  accountSelector,
  isLoadingAccountSelector,
  isUpdatingAccountSelector,
} from '../../../selectors/Account';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import isEmpty from 'lodash/isEmpty';
import AddExpertiseModal from '../../../components/ReviewerOnboarding/AddExpertiseModal';
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons';
import {
  modifySkillsMap,
  removeSkillFromMap,
  prepareSkillsMapForStorage,
  verifySkillsMap,
  SkillsMap,
} from '../../../utils/skillsMapHelpers';
import { User, Skill, ReviewerSkill } from '../../../models/User';
import { logEvent, REVIEWER_ONBOARDING_SETTINGS } from 'utils/analytics';

export interface Props {
  user: User;
  skills: Skill[];
  fetchSkills: () => void;
  isUpdating: boolean;
  handleSubmit: () => void;
  storeReviewerSkills: (skills: { [key: number]: ReviewerSkill }) => void;
  requestDisplayNameChange: (name: string) => void;
  theme: Theme;
  isLoading: boolean;
  isUpdatingAccount: boolean;
}

interface State {
  skillsMap: SkillsMap;
  skillToRemove?: number;
  error?: string;
  displayName?: string;
  showAddSkillModal: boolean;
  showSkillsToast: boolean;
  addedSkills: {};
  skillRemoving?: Skill;
}

export class Settings extends PureComponent<Props, State> {
  state: State = {
    skillsMap: {},
    skillToRemove: undefined,
    error: undefined,
    displayName:
      this.props.user.reviewer_profile?.requested_display_name ??
      this.props.user.reviewer_profile?.display_name,
    showAddSkillModal: false,
    showSkillsToast: false,
    addedSkills: {},
    skillRemoving: undefined,
  };

  componentDidMount() {
    const { skills, fetchSkills } = this.props;
    this.setSkillsMap();
    if (isEmpty(skills)) {
      fetchSkills();
    }

    logEvent(REVIEWER_ONBOARDING_SETTINGS);
  }

  componentDidUpdate(prevProps: Props) {
    const { user, isUpdating, handleSubmit } = this.props;
    if (
      prevProps.user.reviewer_profile?.skills !== user.reviewer_profile?.skills
    ) {
      this.setSkillsMap();
    }
    if (prevProps.isUpdating && !isUpdating) {
      handleSubmit();
    }
  }

  setSkillsMap = () => {
    const { user } = this.props;
    const { reviewer_profile } = user;
    const { skills } = reviewer_profile || {};
    if (user && skills && !isEmpty(skills)) {
      const skillsMap = skills.reduce((acc: SkillsMap, skill: Skill) => {
        acc[skill.id] = skill;
        return acc;
      }, {});

      this.setState({ skillsMap });
    }
  };

  handleChangeYearsExperience = (skillId: number, yearsExperience: number) => {
    const newSkillsMap = modifySkillsMap(
      this.state.skillsMap,
      skillId,
      'years_experience',
      yearsExperience
    );
    this.setState({
      skillsMap: newSkillsMap,
      error: undefined,
    });
  };

  handleChangeSkillLevel = (skillId: number, skillLevel: number) => {
    const newSkillsMap = modifySkillsMap(
      this.state.skillsMap,
      skillId,
      'skill_level',
      skillLevel
    );
    this.setState({
      skillsMap: newSkillsMap,
      error: undefined,
    });
  };

  handleSubmit = () => {
    const { storeReviewerSkills } = this.props;
    const { skillsMap } = this.state;
    if (!verifySkillsMap(skillsMap)) {
      this.setState({
        error:
          'Please select skill level and years of experience for each of your skills.',
      });
      return;
    }

    const preparedSkillsMap = prepareSkillsMapForStorage(skillsMap);
    if (preparedSkillsMap) {
      storeReviewerSkills(preparedSkillsMap);
    }
  };

  setSkillToRemove = (skillId: number | undefined) => {
    this.setState({ skillToRemove: skillId });
  };

  removeSkill = () => {
    const { skillToRemove, skillsMap } = this.state;
    if (skillToRemove !== undefined) {
      this.setState({
        skillsMap: removeSkillFromMap(skillsMap, skillToRemove),
        skillToRemove: undefined,
      });
    }
  };

  getSkillById = (skillId: number): Skill | null => {
    const { user } = this.props;
    const { reviewer_profile } = user;
    const { skills } = reviewer_profile || {};
    if (!user || !skills || isEmpty(skills)) {
      return null;
    }
    return (
      skills.find((skill) => {
        return skill.id === skillId;
      }) || null
    );
  };

  mergeSkillsMaps = (newSkillsMap: { [key: number]: Skill }) => {
    const { skillsMap } = this.state;
    this.setState({
      addedSkills: newSkillsMap,
      skillsMap: { ...skillsMap, ...newSkillsMap },
      showSkillsToast: true,
    });
  };

  submitDisplayName = () => {
    const { displayName } = this.state;
    const { requestDisplayNameChange } = this.props;
    if (displayName) {
      requestDisplayNameChange(displayName);
    }
  };

  render(): JSX.Element {
    const { theme, user, isLoading, isUpdating, isUpdatingAccount, skills } =
      this.props;
    const { reviewer_profile } = user;
    const {
      error,
      skillsMap,
      skillToRemove,
      skillRemoving,
      displayName,
      showAddSkillModal,
      showSkillsToast,
      addedSkills,
    } = this.state;

    return (
      <StepCard
        handleSubmit={this.handleSubmit}
        showPreviousButton={false}
        isUpdating={isUpdating}
      >
        <CardHeader>Code Review Skills</CardHeader>
        <Text variant="body1" color="textSecondary">
          Review and update your skills. Choose skills that you are able and
          willing to do code reviews for.
        </Text>
        <Div mt={4} mb={6}>
          {(!user && isLoading) ||
          !reviewer_profile?.skills ||
          isEmpty(reviewer_profile?.skills) ? (
            <React.Fragment>
              <ContentLoader height="50px" width="100%" mb={2} />
              <ContentLoader height="50px" width="100%" mb={2} />
              <ContentLoader height="50px" width="100%" mb={2} />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <ExpertiseLevels
                key={JSON.stringify(skillsMap)}
                skills={skillsMap}
                onChangeExperienceSelector={this.handleChangeYearsExperience}
                onChangeSkillLevel={this.handleChangeSkillLevel}
                onClickRemove={this.setSkillToRemove}
              />
              <Flex justifyContent="flex-end">
                <Button
                  data-testid="qa-add-skill"
                  variant="contained"
                  color="primary"
                  onClick={() => this.setState({ showAddSkillModal: true })}
                >
                  + Add a Skill
                </Button>
              </Flex>
              {error && (
                <Text variant="body1" color="error" textAlign="center" mb={3}>
                  {error}
                </Text>
              )}
            </React.Fragment>
          )}
          <AddExpertiseModal
            skills={skills}
            currentSkillsMap={skillsMap}
            showModal={showAddSkillModal}
            hideModal={() => this.setState({ showAddSkillModal: false })}
            handleSubmit={this.mergeSkillsMaps}
          />
          <Modal
            variant="alert"
            isOpen={Boolean(skillToRemove)}
            showSecondaryButton={true}
            primaryButtonColor={theme.palette.highlight.main}
            primaryButtonLabel="Yes, remove"
            title="Removing skill"
            onClickPrimaryButton={this.removeSkill}
            onRequestClose={() => this.setSkillToRemove(undefined)}
          >
            <Div width="95%">
              Are you sure you want to remove the
              {` ${skillRemoving?.name} `} skill?
            </Div>
          </Modal>
        </Div>
        <CardHeader>Reviewer Display Name</CardHeader>
        <Flex direction="column">
          <Text variant="body1" color="textSecondary" mb={1}>
            This will be displayed on the comments you post in code reviews.
          </Text>
          <InputField
            id="displayName"
            value={displayName}
            onChange={(event) =>
              this.setState({ displayName: event.target.value })
            }
            width={600}
            endIcon={
              reviewer_profile?.requested_display_name ? (
                <Tooltip
                  title="Your display name change request is being reviewed."
                  placement="top"
                >
                  <Text variant="caption">Request Pending</Text>
                </Tooltip>
              ) : null
            }
          />
          <Button
            data-testid="qa-request-change"
            mt={2}
            variant="contained"
            onClick={this.submitDisplayName}
            color="primary"
            width={200}
            disabled={
              displayName === reviewer_profile?.requested_display_name ||
              displayName === reviewer_profile?.display_name ||
              !displayName
            }
          >
            {isUpdatingAccount ? (
              <Loading size={24} customcolor={theme.palette.common.white} />
            ) : (
              `Submit Change Request`
            )}
          </Button>
        </Flex>
        <Toast
          showToast={showSkillsToast}
          handleClose={() => this.setState({ showSkillsToast: false })}
          severity="success"
          vertical="bottom"
          horizontal="center"
          message={`Success! You've successfully added ${
            Object.keys(addedSkills).length
          } ${Object.keys(addedSkills).length > 1 ? 'skills' : 'skill'}.`}
          icon={faCheckCircle}
        />
      </StepCard>
    );
  }
}

function mapStateToProps(state: any) {
  const { signups } = state;
  const account = accountSelector(state);
  const isLoading = isLoadingAccountSelector(state);
  const isUpdatingAccount = isUpdatingAccountSelector(state);
  return {
    user: account.user,
    isLoading,
    isUpdating: signups.isUpdating,
    isUpdatingAccount,
    skills: signups.skills,
  };
}

function mapDispatchToProps(dispatch: Dispatch<{}>) {
  return bindActionCreators({ ...signupsActions, ...accountActions }, dispatch);
}

export default withTheme(
  connect(mapStateToProps, mapDispatchToProps)(Settings)
);
